import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { observer } from 'mobx-react';
import { isEmpty } from 'lodash';

import { FormStepProgress } from '@shared/FormStepProgress';
import { Loader } from '@shared/Loader';
import { CaseAdapter } from '@shared/CaseAdapter';
import stores from '../../stores';
import { RegistrationTabs } from '../BookingForm/RegistrationTabs';
import { InfoConfirmation } from './components/InfoConfirmation';
import { RecoveryPointType } from '../RecoveryPointsSearchResult/types';
import { formatDate } from '../RecoveryPointsSearchResult/helpers';
import { PaymentConfirmation } from './components/PaymentConfirmation';
import { FinalStep } from './components/FinalStep';
import { PaymentError } from './components/PaymentError';

const Container = styled.div`
  background-color: #fff;
  width: 100%;
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
`;

const steps = ['Coordonnées', 'Paiement', 'Récapitulatif'];

const RecoveryPointsBookingForm = observer(
  ({ point, startStep = 0, centerData, paymentStatus, initialPaymentData }) => {
    const [step, setStep] = useState(startStep);
    const [loading, setLoading] = useState(true);

    const { userInfo } = stores.userStore;
    const {
      paymentData,
      errorText,
      isPointsDataLoading,
      errorStatus,
      promoValidationStatus,
      price,
      newPrice,
    } = stores.recoveryPointsStore;

    const selectedPoint = CaseAdapter.toFrontFormat(JSON.parse(point)) as RecoveryPointType;

    const center = CaseAdapter.toFrontFormat(JSON.parse(centerData));
    const stageDateYear = new Date(selectedPoint.endDate).getFullYear();
    const pointDate = `${formatDate(selectedPoint.startDate)} et ${formatDate(
      selectedPoint.endDate
    )} ${stageDateYear}`;
    const isLoggedIn = !isEmpty(userInfo);

    const onExamSigninFormSubmitHandler = async (data, formikBag) => {
      try {
        await stores.userStore.signIn(data, formikBag);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.log(e);
      } finally {
        setLoading(false);
      }
    };

    const validatePointReservation = async (reservationId: string) => {
      stores.recoveryPointsStore.validatePointReservation(reservationId);
    };

    const validatePromoCode = async (code: string) => {
      stores.recoveryPointsStore.validatePromoCode(paymentData.params?.vads_order_id, code);
    };

    const onExamRegFormSubmitHandler = async (data, formikBag) => {
      try {
        await stores.userStore.signUp(data, formikBag);
      } catch (e) {
        // eslint-disable-next-line no-console
        console.error(e);
      } finally {
        setLoading(false);
      }
    };

    const onConfirmPointSelection = (pointId: string) => {
      stores.recoveryPointsStore.createPointReservation(pointId);
      setStep(1);
    };

    useEffect(() => {
      const fetchUserData = async () => {
        try {
          await stores.userStore.getUser();
        } finally {
          setLoading(false);
        }
      };

      fetchUserData();
    }, []);

    const renderStepContent = () => {
      if (loading || isPointsDataLoading) {
        return <Loader />;
      }

      if (paymentStatus === 'failed' && step === 1) {
        return (
          <PaymentError
            validatePointReservation={validatePointReservation}
            center={center}
            pointDate={pointDate}
            data={initialPaymentData}
          />
        );
      }

      if (errorStatus && step === 1) {
        return (
          <PaymentError
            errorText={errorText}
            errorStatus={errorStatus}
            center={center}
            pointDate={pointDate}
          />
        );
      }

      if (!isLoggedIn && step === 0) {
        return (
          <RegistrationTabs
            formForRecoveryPoints
            onExamRegFormSubmitHandler={onExamRegFormSubmitHandler}
            onExamSigninFormSubmitHandler={onExamSigninFormSubmitHandler}
          />
        );
      }
      if (isLoggedIn && step === 0) {
        window.scrollTo(0, 0);
        return (
          <InfoConfirmation
            onButtonClick={onConfirmPointSelection}
            pointDate={pointDate}
            userInfo={userInfo}
            pointId={selectedPoint.id}
            center={center}
          />
        );
      }

      switch (step) {
        case 1:
          return (
            <PaymentConfirmation
              validatePointReservation={validatePointReservation}
              paymentData={paymentData}
              errorStatus={errorStatus}
              validatePromoCode={validatePromoCode}
              promoValidationStatus={promoValidationStatus}
              price={price}
              newPrice={newPrice}
            />
          );
        case 2:
          return <FinalStep pointDate={pointDate} />;
        default:
          return null;
      }
    };

    return (
      <Container>
        <FormStepProgress steps={steps} currentStep={step} />
        {renderStepContent()}
      </Container>
    );
  }
);

export { RecoveryPointsBookingForm };
